'use client'

import { useEffect, useRef, useState } from 'react'
import { useLowStock } from '@/components/menu/LowStockContext'
import { LowStockBadge } from '@/components/menu/LowStockBadge'
import Image from 'next/image'
import { Plus, Clock } from 'lucide-react'
import { AnimatePresence } from 'framer-motion'
import { useTranslations, useLocale } from 'next-intl'
import type { ZonedClientFacts } from '@/lib/menu/zonedClientFacts'
import { ItemModal } from './ItemModal'
import { GuidedItemFlow } from './GuidedItemFlow'
import { micrositeOpensGuidedFlow, retailCardPrice, retailPickerSoldOut, tapOpensItemSheet, type RetailPickerFacts } from '@/lib/microsite/retailLine'
import { SectionHeader } from './SectionHeader'
import { menuPhotoUrl } from '@/lib/utils/imageTransform'
import { focalPosition } from '@/lib/menu/photoFocal'
import type { CartItem } from './types'
import { effectiveItemCardPrice, type PricingConfig } from '@/lib/utils/pricing'
import { isItemAvailableNow, availabilityLabel, type ItemAvailability } from '@/lib/menu/availability'
import { itemHasUnsellableRequiredGroup } from '@/lib/menu/soldOutGroups'
import { localizedItem, type ItemTranslations } from '@/lib/menu/localizeItem'
import { track } from '@/lib/tracking/storefrontEvents'
import { FEATURED_AUTOPLAY_MS, nextSlide, pickFeatured, slideAtScroll } from '@/lib/microsite/featuredCarousel'

type ModifierOption = {
  id: string
  name: string
  price_delta: number
  is_available: boolean | null
}

type ModifierGroup = {
  id: string
  name: string
  required: boolean | null
  min_selections: number | null
  max_selections: number | null
  /** 'normal' | 'removal' | 'allergy_alert'. La lee GuidedItemFlow (vía
   *  classifyOption) para decidir Agregar/Quitar/pregunta — sin ella, TS no
   *  avisaría si algún día la consulta deja de traerla. */
  display_style?: string | null
  modifier_options: ModifierOption[]
}

type Item = {
  id: string
  name: string
  description: string | null
  translations?: ItemTranslations | null
  price: number
  image_url: string | null
  image_focal?: string | null
  is_available: boolean | null
  category_id: string | null
  /** Toggle "Destacado en Pantalla del Customer" del editor del menú. */
  is_display_featured?: boolean | null
  available_from?: string | null
  available_until?: string | null
  available_days?: number[] | null
  /** Pizza armable por zonas (mitad/mitad). Solo el ItemModal legado la soporta —
   *  ver el uso de `zoned_build` más abajo, antes de montar el modal. */
  zoned_build?: boolean | null
  item_modifier_groups?: Array<{
    /** Posición del grupo DENTRO de este ítem (definida por el dueño). Sin
     *  ella, buildGuidedSteps cae al orden sin garantías de PostgREST. */
    sort_order?: number | null
    modifier_groups: ModifierGroup | null
  }>
} & RetailPickerFacts

type Props = {
  items: Item[]
  pricingCfg: PricingConfig
  /** Rejilla precio×tamaño y modo de precio por zona; se reenvía tal cual al modal.
   *  Obligatoria a propósito: si un sitio nuevo la olvidara, la pizza volvería a
   *  mostrar el precio plano del ingrediente sin que nadie lo note. */
  zonedFacts: ZonedClientFacts
  accentColor: string
  /** Para los chips de quick_notes en el modal del ítem. */
  /** TZ del local — para evaluar la ventana de venta (día/hora) del ítem. */
  timezone?: string | null
  onAddToCart: (item: CartItem) => void
  /** Flujo de venta del local ('classic' | 'guided'). La decisión de qué modal
   *  abrir se toma con `usesGuidedFlow`, que además excluye los platos zonados.
   *  Obligatoria a propósito: sin ella un caller nuevo compila y cae en
   *  clásico en silencio — exactamente el fallo mudo que este ajuste evita. */
  salesFlow: string | null
  /** Local de la tienda: el modal de un producto por peso o con variantes pide su
   *  stock EN VIVO con él (Tarea 2b.3). Un plato no lo usa. */
  locationId?: string
}

function itemAvailability(item: Item): ItemAvailability {
  return {
    available_from: item.available_from,
    available_until: item.available_until,
    available_days: item.available_days,
  }
}

/**
 * Sección "Destacados": carrusel automático de platillos, como teaser de
 * bienvenida antes del menú completo (antes eran 3 tarjetas en rejilla y los
 * clientes creían que eso era todo el menú — pedido de Samuel, 2026-10-19).
 * Hasta FEATURED_MAX tarjetas; en el teléfono se asoma la siguiente para que se
 * lea como carrusel. Pasa sola cada FEATURED_AUTOPLAY_MS y se detiene mientras
 * el cliente la toca, cuando no está en pantalla, con el modal abierto o con
 * prefers-reduced-motion. Composición distinta a MenuSection (foto a
 * sangre con el nombre/precio encima). Theme-aware (tokens --ms-*).
 *
 * Selección: ítems marcados como Destacado (`is_display_featured`, toggle del
 * editor del menú) que además tengan foto — la sección es visual. Si el merchant
 * no marcó ninguno (o marcó <2), cae al heurístico anterior (primeros ítems con
 * foto) para no dejar vacía la sección en storefronts que nunca tocaron el
 * toggle. Degrada (no renderiza) si hay <2 candidatos con foto.
 *
 * Las cards son funcionales: al tocarlas agregan al carrito igual que en el
 * menú (abren el modal de modificadores si el ítem los tiene).
 */
export function FeaturedSection({ items, pricingCfg, zonedFacts, accentColor, timezone, onAddToCart, salesFlow, locationId }: Props) {
  const t = useTranslations('public.microsite')
  const lowStock = useLowStock()
  const locale = useLocale() === 'en' ? 'en' : 'es'
  const tz = timezone ?? 'UTC'
  const [modalItem, setModalItem] = useState<Item | null>(null)

  // Un plato agotado (86) no puede ser el teaser de bienvenida: desde que la
  // carta muestra los agotados (en vez de ocultarlos), acá sí se filtran — la
  // sección es un showcase clickeable que agrega al carrito.
  // Súper online (R6b): un producto con variantes y ninguna activa tampoco.
  const featured = pickFeatured(items, (i) => !itemHasUnsellableRequiredGroup(i) && !retailPickerSoldOut(i))

  // ── Carrusel ──────────────────────────────────────────────────────────────
  // Hooks ANTES del return temprano de abajo (sección vacía).
  const sectionRef = useRef<HTMLElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const [touching, setTouching] = useState(false)
  const [onScreen, setOnScreen] = useState(false)
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const count = featured.length

  useEffect(() => {
    const el = sectionRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.4 })
    io.observe(el)
    return () => io.disconnect()
  }, [count])

  function goTo(i: number) {
    const track = trackRef.current
    const card = track?.children[i] as HTMLElement | undefined
    if (!track || !card) return
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    // offsetLeft de las dos tarjetas comparte el mismo offsetParent: la resta es
    // la distancia dentro de la pista (el padding lo absorbe scroll-padding).
    const first = track.firstElementChild as HTMLElement
    track.scrollTo({ left: card.offsetLeft - first.offsetLeft, behavior: reduce ? 'auto' : 'smooth' })
  }

  useEffect(() => {
    if (count < 2 || touching || !onScreen || modalItem) return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    const id = setTimeout(() => goTo(nextSlide(active, count)), FEATURED_AUTOPLAY_MS)
    return () => clearTimeout(id)
  }, [active, count, touching, onScreen, modalItem])

  useEffect(() => () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current)
  }, [])

  function pause() {
    if (resumeTimer.current) clearTimeout(resumeTimer.current)
    setTouching(true)
  }
  // Retoma unos segundos después de que el cliente suelta el carrusel.
  function resumeSoon() {
    if (resumeTimer.current) clearTimeout(resumeTimer.current)
    resumeTimer.current = setTimeout(() => setTouching(false), 6000)
  }

  function onTrackScroll() {
    const track = trackRef.current
    if (!track) return
    const first = (track.firstElementChild as HTMLElement | null)?.offsetLeft ?? 0
    const offsets = Array.from(track.children, (c) => (c as HTMLElement).offsetLeft - first)
    const i = slideAtScroll(track.scrollLeft, offsets, track.scrollWidth - track.clientWidth)
    if (i !== active) setActive(i)
  }

  if (count < 2) return null

  function handleCardTap(item: Item) {
    if (!isItemAvailableNow(itemAvailability(item), tz)) return
    // Súper online (R6b): sin variantes activas, o por peso sin unidad válida, no se
    // abre un selector vacío.
    if (retailPickerSoldOut(item)) return
    // Plato con modificadores (main) o producto por peso / con variantes (súper
    // online, solo con online_retail_v2): abre el modal en vez de agregar directo.
    if (tapOpensItemSheet(item)) {
      setModalItem(item)
      // Medición de anuncios (no-op sin pixel): «vio el plato» = abrió su detalle.
      const price = effectiveItemCardPrice(item.price, pricingCfg)
      track('view_item', { value: price, currency: 'USD', items: [{ id: item.id, name: item.name, price, quantity: 1 }] })
      return
    }
    const cardPrice = effectiveItemCardPrice(item.price, pricingCfg)
    onAddToCart({
      // eslint-disable-next-line react-hooks/purity
      cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      itemId: item.id,
      name: item.name,
      price: cardPrice,
      quantity: 1,
      modifiers: [],
    })
  }

  return (
    <section
      ref={sectionRef}
      className="px-5 sm:px-8 pt-8 sm:pt-14 pb-2 max-w-6xl mx-auto"
      aria-roledescription="carousel"
      data-testid="featured-carousel"
    >
      <SectionHeader number="00" kicker={t('featuredKicker')} title={t('featuredTitle')} />

      <div
        ref={trackRef}
        onScroll={onTrackScroll}
        onPointerDown={pause}
        onPointerUp={resumeSoon}
        onPointerCancel={resumeSoon}
        onMouseEnter={pause}
        onMouseLeave={resumeSoon}
        onFocus={pause}
        onBlur={resumeSoon}
        className="flex gap-3 sm:gap-6 mt-5 sm:mt-8 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0 scroll-px-5 sm:scroll-px-0"
      >
        {featured.map((item) => {
          const cardPrice = effectiveItemCardPrice(item.price, pricingCfg)
          // Súper online (R6a): «$3.99/lb» / «desde $2.50». Un plato da null: la rama de main.
          const retailPrice = retailCardPrice(item, pricingCfg)
          const display = localizedItem(item, locale)
          const availableNow = isItemAvailableNow(itemAvailability(item), tz)
          const schedLabel = availabilityLabel(itemAvailability(item), locale)
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleCardTap(item)}
              disabled={!availableNow}
              aria-roledescription="slide"
              className={`group relative shrink-0 snap-start w-[78%] sm:w-[calc((100%-3rem)/3)] text-left overflow-hidden transition ${availableNow ? 'cursor-pointer hover:-translate-y-1 active:scale-[0.99]' : 'cursor-not-allowed'}`}
              style={{
                borderRadius: 'var(--ms-radius)',
                boxShadow: 'var(--ms-card-shadow)',
                border: '1px solid var(--ms-border)',
              }}
            >
              <div className="relative w-full overflow-hidden aspect-square sm:aspect-[4/5]">
                <Image
                  src={menuPhotoUrl(item.image_url as string, 700)}
                  alt={display.name}
                  fill
                  sizes="(max-width: 640px) 80vw, 33vw"
                  className="object-cover transition duration-500 group-hover:scale-[1.05]"
                  style={{ objectPosition: focalPosition(item.image_focal), filter: availableNow ? undefined : 'grayscale(1)' }}
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.05) 52%, transparent 100%)' }} />
                {schedLabel && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-sm"
                    style={{ background: availableNow ? 'rgba(17,17,17,0.82)' : 'var(--ms-accent)' }}
                  >
                    <Clock className="w-3 h-3" strokeWidth={2.5} />{schedLabel}
                  </span>
                )}
                {availableNow && lowStock.item(item.id) && (
                  // Arriba a la izquierda: abajo va el nombre sobre el degradado.
                  <span className="absolute top-3 left-3">
                    <LowStockBadge size="lg" />
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                  <h3 className="text-white text-[19px] sm:text-[22px] font-bold leading-tight" style={{ fontFamily: 'var(--ms-font-display)', textShadow: '0 2px 14px rgba(0,0,0,0.4)' }}>
                    {display.name}
                  </h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-white text-[16px] font-bold">
                      {retailPrice
                        ? `${retailPrice.from ? `${t('priceFrom')} ` : ''}$${retailPrice.cardPrice.toFixed(2)}${retailPrice.unit ? `/${retailPrice.unit}` : ''}`
                        : <>${cardPrice.toFixed(2)}</>}
                    </span>
                    {availableNow ? (
                      <span
                        className="inline-flex items-center gap-1 text-white text-[11px] font-bold uppercase tracking-[0.1em] rounded-full px-3 py-1.5"
                        style={{ background: 'var(--ms-accent)' }}
                      >
                        {t('add')} <Plus className="w-3.5 h-3.5" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-white/90 text-[11px] font-bold rounded-full px-3 py-1.5" style={{ background: 'rgba(0,0,0,0.45)' }}>
                        <Clock className="w-3.5 h-3.5" />{schedLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Puntos: dicen "hay más" y permiten saltar a una tarjeta. */}
      <div className="flex justify-center gap-1.5 mt-3" role="tablist">
        {featured.map((item, i) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={t('featuredGoTo', { n: i + 1, total: count })}
            onClick={() => {
              pause()
              goTo(i)
              resumeSoon()
            }}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === active ? 18 : 6,
              background: i === active ? 'var(--ms-accent)' : 'var(--ms-border)',
            }}
          />
        ))}
      </div>

      <AnimatePresence>
        {modalItem && (
          micrositeOpensGuidedFlow(salesFlow, modalItem) ? (
            <GuidedItemFlow
              item={modalItem}
              pricingCfg={pricingCfg}
              accentColor={accentColor}
              onAdd={(cartItem) => {
                onAddToCart(cartItem)
                setModalItem(null)
              }}
              onClose={() => setModalItem(null)}
            />
          ) : (
            <ItemModal
              key={modalItem.id}
              item={modalItem}
              pricingCfg={pricingCfg}
              zonedFacts={zonedFacts}
              accentColor={accentColor}
              locationId={locationId}
              onAdd={(cartItem) => {
                onAddToCart(cartItem)
                setModalItem(null)
              }}
              onClose={() => setModalItem(null)}
            />
          )
        )}
      </AnimatePresence>
    </section>
  )
}
